import React, { useMemo, useState } from 'react';
import { Container, Card, Table, Form, Badge, Alert, Spinner, ButtonGroup, Button } from 'react-bootstrap';
import {
  useGetFirmOfficesQuery,
  useMapFirmOfficeMutation,
  useSetFirmDisplayNameMutation,
} from '../app/api';

// Super Admin -> Firm & FSC Offices.
//
// A B2B ("entrepreneur") firm and an FSC entrepreneur office are the same real
// office. They exist as two unrelated name lists only because the B2B surface
// shipped first, with typed-in firm names, before anyone knew what those
// offices were called in Salesforce. This screen reconciles them.
//
// Mapping a firm also classifies its office as 'entrepreneur', which is what
// makes it visible to everyone scoped `entrepreneur_all` — so this page grants
// access as a side effect, which is why the banner says so out loud.

export default function FirmOffices() {
  const { data, isLoading, isError, refetch } = useGetFirmOfficesQuery();
  const [mapFirm] = useMapFirmOfficeMutation();
  const [setDisplayName] = useSetFirmDisplayNameMutation();

  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const firms = data?.firms || [];
  const offices = data?.offices || [];

  const mappedCount = firms.filter(f => f.fsc_office_id).length;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return firms;
    return firms.filter(f =>
      (f.name || '').toLowerCase().includes(q) ||
      (f.fsc_office_name || '').toLowerCase().includes(q));
  }, [firms, query]);

  // Every mutation is followed by refetch(): RTK tagTypes are inert app-wide in
  // this app, so invalidation alone would leave the table stale. Refetching also
  // refreshes the office picker, which shrinks as offices get claimed.
  const run = async (firmId, fn) => {
    setBusyId(firmId);
    setError('');
    try {
      await fn().unwrap();
      await refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Something went wrong.');
    } finally {
      setBusyId(null);
    }
  };

  const handleMap = (firm, value) =>
    run(firm.id, () => mapFirm({ firm_id: firm.id, fsc_office_id: value || null }));

  const handleName = (firm, source) =>
    run(firm.id, () => setDisplayName({ firm_id: firm.id, source }));

  if (isLoading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container className="py-4">
        <Alert variant="danger">Could not load firms and offices.</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <h3 className="mb-1">Firms &amp; FSC Offices</h3>
      <p className="text-muted" style={{ maxWidth: '46rem' }}>
        A B2B firm and an FSC entrepreneur office are the same office — they only
        have two names because the B2B side was built before we knew the
        Salesforce names. Map each one here, then choose which name it displays
        under.
      </p>

      <Alert variant="warning" className="d-flex gap-2 align-items-start">
        <i className="bi bi-shield-lock mt-1" />
        <div>
          <strong>Mapping grants access.</strong> Linking a firm to an office also
          marks that office as an <em>entrepreneur</em> office, which makes its
          clients visible to everyone with the “All entrepreneur offices” scope.
          Only map an office you can confirm is that firm.
        </div>
      </Alert>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
        <Form.Control
          style={{ maxWidth: '20rem' }}
          placeholder="Search firms or offices…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <span className="text-muted" style={{ fontSize: '0.85rem' }}>
          {mappedCount} of {firms.length} firms mapped · {offices.length} offices unclaimed
        </span>
      </div>

      <Card>
        <Table responsive hover className="mb-0 align-middle">
          <thead>
            <tr>
              <th style={{ width: '24%' }}>Hub firm name</th>
              <th style={{ width: '30%' }}>FSC office</th>
              <th style={{ width: '24%' }}>Display name</th>
              <th>Shows as</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(firm => {
              const busy = busyId === firm.id;
              const mapped = Boolean(firm.fsc_office_id);
              return (
                <tr key={firm.id}>
                  <td>
                    <div className="fw-semibold">{firm.name}</div>
                    {!mapped && (
                      // Unmapped is a supported state, not an error: the firm may
                      // simply not be in Salesforce yet (no AUM means no Orion
                      // record, so no FSC office), or it may be test data.
                      <Badge bg="light" text="dark" className="border">Not in Salesforce</Badge>
                    )}
                  </td>

                  <td>
                    <Form.Select
                      size="sm"
                      disabled={busy}
                      value={firm.fsc_office_id || ''}
                      onChange={e => handleMap(firm, e.target.value)}
                    >
                      <option value="">— Not mapped —</option>
                      {/* The firm's own office stays listed: the server excludes
                          claimed offices from the picker, so without this the
                          current value would have nothing to render against. */}
                      {mapped && (
                        <option value={firm.fsc_office_id}>{firm.fsc_office_name}</option>
                      )}
                      {offices.map(o => (
                        <option key={o.fsc_id} value={o.fsc_id}>{o.name}</option>
                      ))}
                    </Form.Select>
                  </td>

                  <td>
                    <ButtonGroup size="sm">
                      <Button
                        variant={firm.display_name_source === 'hub' ? 'primary' : 'outline-secondary'}
                        disabled={busy}
                        onClick={() => handleName(firm, 'hub')}
                      >
                        Hub
                      </Button>
                      <Button
                        variant={firm.display_name_source === 'fsc' ? 'primary' : 'outline-secondary'}
                        // Nothing to show without a mapping, so the choice is
                        // unavailable rather than silently ineffective.
                        disabled={busy || !mapped}
                        onClick={() => handleName(firm, 'fsc')}
                      >
                        Salesforce
                      </Button>
                    </ButtonGroup>
                  </td>

                  <td>
                    {busy
                      ? <Spinner animation="border" size="sm" />
                      : <span className="fw-semibold">{firm.display_name}</span>}
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center text-muted py-4">
                  No firms match “{query}”.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>
    </Container>
  );
}
